import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import apiService from '../../services/api.service';
import { CopyableJob, CopyState, copyResultMessage, copyStates, defaultSelection } from '../../utils/scheduleCopy';

export interface CopyCompany {
  CompanyID: number;
  CompanyCode: string;
  CompanyName: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  companies: CopyCompany[];
  /** The page's label of an endpoint key ("SAP B1 Items (ItemMaster)"). */
  endpointLabel: (key: string) => string;
  /** The company to copy from when the dialog opens (the page's company, when it has jobs), if any. */
  defaultFromCompanyId: number | null;
  /** The company to copy to when the dialog opens (the page's company, when it has none yet), if any. */
  defaultToCompanyId?: number | null;
  /** After a copy: the message to show, and the company copied to. */
  onCopied: (message: string, toCompanyId: number) => void;
}

const STATE_TEXT: Record<CopyState, string> = {
  new: 'Not there yet',
  'same-data': 'Has a job for this data',
  'same-job': 'Already there',
  duplicate: 'Same as a job above',
};

const asList = (res: any): CopyableJob[] => (Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []);
const errorText = (e: any): string => e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Something went wrong';

/** Schedule Jobs › Copy to company: copies jobs from one company to another, so a new company needs no setup by hand. */
const CopySchedulesDialog: React.FC<Props> = ({
  open, onClose, companies, endpointLabel, defaultFromCompanyId, defaultToCompanyId = null, onCopied,
}) => {
  const [fromId, setFromId] = useState<number | ''>('');
  const [toId, setToId] = useState<number | ''>('');
  const [source, setSource] = useState<CopyableJob[]>([]);
  const [target, setTarget] = useState<CopyableJob[]>([]);
  const [loadingSource, setLoadingSource] = useState(false);
  const [loadingTarget, setLoadingTarget] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [startPaused, setStartPaused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const companyById = useMemo(() => new Map(companies.map((c) => [Number(c.CompanyID), c])), [companies]);
  const label = (c?: CopyCompany) => (c ? `${c.CompanyCode} · ${c.CompanyName}` : '');

  useEffect(() => {
    if (!open) return;
    const from = defaultFromCompanyId && companyById.has(defaultFromCompanyId) ? defaultFromCompanyId : '';
    const to = defaultToCompanyId && companyById.has(defaultToCompanyId) && defaultToCompanyId !== from ? defaultToCompanyId : '';
    setFromId(from);
    setToId(to);
    setStartPaused(false);
    setError(null);
  }, [open, defaultFromCompanyId, defaultToCompanyId, companyById]);

  useEffect(() => {
    if (!open || fromId === '') {
      setSource([]);
      return;
    }
    let live = true;
    setLoadingSource(true);
    setError(null);
    apiService.listSchedulesForCompany(Number(fromId))
      .then((res) => { if (live) setSource(asList(res)); })
      .catch((e) => { if (live) { setSource([]); setError(errorText(e)); } })
      .finally(() => { if (live) setLoadingSource(false); });
    return () => { live = false; };
  }, [open, fromId]);

  useEffect(() => {
    if (!open || toId === '') {
      setTarget([]);
      return;
    }
    let live = true;
    setLoadingTarget(true);
    apiService.listSchedulesForCompany(Number(toId))
      .then((res) => { if (live) setTarget(asList(res)); })
      .catch((e) => { if (live) { setTarget([]); setError(errorText(e)); } })
      .finally(() => { if (live) setLoadingTarget(false); });
    return () => { live = false; };
  }, [open, toId]);

  const states = useMemo(() => copyStates(source, toId === '' ? [] : target), [source, target, toId]);
  useEffect(() => { setSelected(defaultSelection(states)); }, [states]);

  const loading = loadingSource || loadingTarget;
  const blocked = (id: number) => { const st = states.get(id); return st === 'same-job' || st === 'duplicate'; };
  const selectable = source.filter((s) => !blocked(s.scheduleid));
  const picked = selectable.filter((s) => selected.has(s.scheduleid));
  const allPicked = selectable.length > 0 && picked.length === selectable.length;
  const toggle = (id: number) => setSelected((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allPicked ? new Set() : new Set(selectable.map((s) => s.scheduleid)));
  const canCopy = fromId !== '' && toId !== '' && fromId !== toId && picked.length > 0 && !loading && !busy;

  const copy = async () => {
    if (!canCopy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiService.copySchedules({
        fromCompanyId: Number(fromId),
        toCompanyId: Number(toId),
        scheduleIds: picked.map((s) => s.scheduleid),
        startPaused,
      });
      const created = Array.isArray(res?.created) ? res.created.length : 0;
      const skipped = Array.isArray(res?.skipped) ? res.skipped.length : 0;
      onCopied(copyResultMessage(created, skipped, label(companyById.get(Number(toId))) || 'the other company'), Number(toId));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth aria-labelledby="copy-schedules-title">
      <DialogTitle id="copy-schedules-title">Copy schedule jobs to another company</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              select
              fullWidth
              size="small"
              id="copy-schedules-from"
              label="From company"
              value={fromId}
              onChange={(e) => { const v = Number(e.target.value); setFromId(v); if (v === toId) setToId(''); }}
              disabled={busy}
            >
              {companies.map((c) => (
                <MenuItem key={c.CompanyID} value={c.CompanyID} disabled={c.CompanyID === toId}>{label(c)}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              id="copy-schedules-to"
              label="To company"
              value={toId}
              onChange={(e) => { const v = Number(e.target.value); setToId(v); if (v === fromId) setFromId(''); }}
              disabled={busy}
            >
              {companies.map((c) => (
                <MenuItem key={c.CompanyID} value={c.CompanyID} disabled={c.CompanyID === fromId}>{label(c)}</MenuItem>
              ))}
            </TextField>
          </Stack>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {loading ? <LinearProgress aria-label="Loading the jobs" /> : null}

          {fromId === '' ? (
            <Typography variant="body2" color="text.secondary">Choose the company whose jobs you want to copy.</Typography>
          ) : !loadingSource && source.length === 0 ? (
            <Alert severity="info">{label(companyById.get(Number(fromId)))} has no schedule jobs to copy.</Alert>
          ) : source.length > 0 ? (
            <TableContainer sx={{ maxHeight: { xs: 'none', sm: 380 }, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader aria-label="Jobs to copy">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allPicked}
                        indeterminate={picked.length > 0 && !allPicked}
                        onChange={toggleAll}
                        disabled={busy || selectable.length === 0}
                        inputProps={{ 'aria-label': 'Pick every job' }}
                      />
                    </TableCell>
                    <TableCell>Job</TableCell>
                    <TableCell>SAP data</TableCell>
                    <TableCell>Runs (cron)</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>In {toId === '' ? 'the other company' : companyById.get(Number(toId))?.CompanyCode || 'the other company'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {source.map((s) => {
                    const st = states.get(s.scheduleid) || 'new';
                    const off = blocked(s.scheduleid);
                    return (
                      <TableRow key={s.scheduleid} hover={!off} sx={off ? { opacity: 0.55 } : undefined}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={!off && selected.has(s.scheduleid)}
                            onChange={() => toggle(s.scheduleid)}
                            disabled={busy || off}
                            inputProps={{ 'aria-label': `Copy ${s.name}` }}
                          />
                        </TableCell>
                        <TableCell sx={{ wordBreak: 'break-word' }}>{s.name}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-word' }}>{endpointLabel(s.endpointkey)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box component="span" sx={{ fontFamily: 'Consolas, monospace' }}>{s.cronexpression}</Box>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" label={s.status} color={s.status === 'Active' ? 'success' : 'warning'} />
                        </TableCell>
                        <TableCell>
                          {toId === '' && st !== 'duplicate' ? '—' : (
                            <Typography variant="caption" color={st === 'same-data' ? 'warning.main' : 'text.secondary'}>{STATE_TEXT[st]}</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}

          <FormControlLabel
            control={<Checkbox size="small" checked={startPaused} onChange={(e) => setStartPaused(e.target.checked)} disabled={busy} />}
            label="Start the copies paused, to switch each one on later"
          />
          <Typography variant="caption" color="text.secondary">
            Each copy keeps its fields, condition, date fields, timing and backfill. Its run history stays with the original,
            and a backfill starts again from its start date. A job the other company already has with the same settings is
            never copied twice. Check the first run of each copy: a field or condition that only the first company has may
            need editing.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={() => { void copy(); }} disabled={!canCopy}>
          {busy ? 'Copying…' : `Copy ${picked.length} job${picked.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default CopySchedulesDialog;
